/**
 * Anexos de solicitação/tarefa — fonte única do caminho do arquivo, do envio e
 * do vínculo com a tarefa.
 *
 * Existe por causa da solicitação 4906 ("Aba de anexar documento indisponível",
 * Alice Karoline, 31/08/2026): o anexo funcionava quase sempre e falhava ~1 vez
 * a cada 100, sem ninguém conseguir reproduzir. Não era uma falha — eram três,
 * todas de concorrência, e por isso invisíveis em teste manual:
 *
 * 1. COLISÃO DE NOME. O caminho era `Date.now()_nome.ext`. Dois arquivos de
 *    mesmo nome escolhidos juntos caem no mesmo milissegundo, geram o mesmo
 *    caminho, e o segundo `upload` (sem upsert) volta "already exists".
 *
 * 2. PERDA POR SOBRESCRITA — a pior. O código montava a lista nova a partir do
 *    array que a TELA tinha em memória e gravava tudo por cima:
 *        novaLista = [...task.anexos_url, novaUrl]
 *    Quem anexasse com a tela aberta há algum tempo apagava, sem aviso, os
 *    anexos que outra pessoa tinha acrescentado no meio. O arquivo continuava
 *    no bucket e sumia da solicitação — exatamente o relato "anexei e não
 *    apareceu". Duas pessoas na mesma solicitação é o normal da Central.
 *
 * 3. ARQUIVO ÓRFÃO. Upload bem-sucedido + gravação falha = arquivo no bucket
 *    que ninguém alcança, e erro na tela. Sem nenhuma nova tentativa: qualquer
 *    oscilação de rede virava falha definitiva.
 *
 * O conserto de (2) é o único que não cabe no front: o acréscimo passa a ser
 * feito pelo próprio Postgres (`array_append` dentro da função
 * `anexar_arquivo_tarefa`), que lê e grava na mesma instrução. Ver a migration
 * 20260921180000.
 */

import { supabase } from './supabase';
import {
    BUCKET_ANEXOS,
    TENTATIVAS_UPLOAD,
    caminhoAnexo,
    valeRepetir,
} from './anexosTarefaRegras.ts';

// Reexporta para quem consome só o módulo principal (telas).
export { BUCKET_ANEXOS, TENTATIVAS_UPLOAD, caminhoAnexo, valeRepetir } from './anexosTarefaRegras.ts';
export { nomeExibicaoAnexo } from './anexosTarefaRegras.ts';

const espera = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/**
 * Sobe o arquivo e devolve a URL pública.
 *
 * Repete em falha transitória, com caminho NOVO a cada tentativa: repetir com o
 * mesmo caminho depois de um upload que na verdade completou (resposta perdida)
 * daria "already exists" e transformaria sucesso em erro.
 *
 * ⚠️ Quem prova o upload é o `data.path` de retorno, nunca o `getPublicUrl` —
 * esse último monta a URL por concatenação e devolve string mesmo para arquivo
 * que não existe.
 */
export async function enviarAnexo(
    file: File,
    /** Pasta dentro do bucket, sem barra no fim. Ex.: `comentarios/4906`. */
    prefixo = '',
): Promise<{ url: string; path: string }> {
    let ultimoErro: unknown = null;

    for (let tentativa = 1; tentativa <= TENTATIVAS_UPLOAD; tentativa++) {
        const path = (prefixo ? `${prefixo.replace(/\/+$/, '')}/` : '') + caminhoAnexo(file.name);
        const { data, error } = await supabase.storage.from(BUCKET_ANEXOS).upload(path, file);

        if (!error && data?.path) {
            const { data: pub } = supabase.storage.from(BUCKET_ANEXOS).getPublicUrl(data.path);
            return { url: pub.publicUrl, path: data.path };
        }

        ultimoErro = error ?? new Error('Upload não devolveu o caminho do arquivo.');
        if (!valeRepetir(ultimoErro) || tentativa === TENTATIVAS_UPLOAD) break;

        await espera(400 * tentativa);
    }

    throw new Error(
        'Não foi possível enviar o arquivo: '
        + ((ultimoErro as any)?.message || String(ultimoErro))
    );
}

/** Apaga do bucket. Best-effort: usado para não deixar órfão quando o vínculo falha. */
export async function descartarAnexo(path: string): Promise<void> {
    try {
        await supabase.storage.from(BUCKET_ANEXOS).remove([path]);
    } catch {
        // Órfão é ruim, mas menos que mascarar o erro real que trouxe o código aqui.
    }
}

/**
 * Sobe o arquivo E o vincula à tarefa, atomicamente.
 *
 * Devolve a lista COMPLETA de anexos como o banco a tem depois do acréscimo —
 * não a lista da tela mais um. É isso que faz o anexo de outra pessoa aparecer
 * em vez de ser apagado.
 */
export async function anexarArquivoNaTarefa(tarefaId: number | string, file: File): Promise<string[]> {
    const { url, path } = await enviarAnexo(file);

    const { data, error } = await supabase.rpc('anexar_arquivo_tarefa', {
        p_tarefa_id: Number(tarefaId),
        p_url: url,
    });

    if (error) {
        // O arquivo subiu mas não pertence a ninguém: tira do bucket antes de
        // avisar, senão cada tentativa frustrada deixa lixo permanente lá.
        await descartarAnexo(path);
        throw new Error(
            'O arquivo foi enviado mas não pôde ser vinculado à solicitação: '
            + error.message + '. Nada foi anexado — tente de novo.'
        );
    }

    return (data as string[] | null) ?? [];
}

/**
 * Tira um anexo da tarefa, atomicamente, e apaga o arquivo.
 *
 * Mesma razão do acréscimo: filtrar a lista da tela e gravar por cima devolvia
 * anexos já removidos por outra pessoa, ou apagava os que ela acabara de pôr.
 */
export async function removerAnexoDaTarefa(tarefaId: number | string, url: string): Promise<string[]> {
    const { data, error } = await supabase.rpc('remover_arquivo_tarefa', {
        p_tarefa_id: Number(tarefaId),
        p_url: url,
    });

    if (error) throw new Error('Não foi possível remover o anexo: ' + error.message);

    const path = url.split(`/${BUCKET_ANEXOS}/`)[1];
    if (path) await descartarAnexo(decodeURIComponent(path));

    return (data as string[] | null) ?? [];
}
